import db, { createRef } from '../../../../db'
import { converter2 } from '../../../../db/converter'
import { Collaboration, Room } from '../../../models/Chat'
import { UserCompanies } from '../../../models/User'
import * as lc from '../../localstorage'
import { IRoomRepository, QueuedRoom } from './IRoomRepository'
import { arrayUnion } from 'firebase/firestore'

/**
 * Room Repository Implementation
 * Contains all Firestore queries for the rooms subcollection
 * Migrated from src/db/serviceRoom.tsx
 */
class RoomRepositoryImpl implements IRoomRepository {
  private static instance: RoomRepositoryImpl

  static getInstance(): RoomRepositoryImpl {
    if (!this.instance) {
      this.instance = new RoomRepositoryImpl()
    }
    return this.instance
  }

  // ============================================================================
  // QUERY METHODS
  // ============================================================================

  /**
   * Get all rooms for a collaboration
   * Migrated from: serviceRoom.getRoomByCollaborationId
   */
  async getByCollaborationId(
    collaborationDocId: string
  ): Promise<Room[] | undefined> {
    try {
      const collaboration = await db
        .collection('collaborations')
        .doc(collaborationDocId)
        .collection('rooms')
        .withConverter(converter2<Room>())
        .get()

      if (collaboration.empty) {
        return undefined
      }

      return collaboration.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }))
    } catch (error) {
      console.error('Error in getByCollaborationId:', error)
      return undefined
    }
  }

  /**
   * Get a single room by ID
   * Migrated from: serviceRoom.findRoomById
   */
  async findById(
    collaborationDocId: string,
    roomsId: string
  ): Promise<Room | undefined> {
    try {
      const collaboration = await db
        .collection('collaborations')
        .doc(collaborationDocId)
        .collection('rooms')
        .doc(roomsId)
        .withConverter(converter2<Room>())
        .get()

      if (collaboration.exists) {
        return { ...collaboration.data(), id: collaboration.id } as Room
      }
      return undefined
    } catch (error) {
      console.error('Error in findById:', error)
      return undefined
    }
  }

  /**
   * Get all queued rooms
   * Migrated from: serviceRoom.getQueuedRooms
   */
  async getQueuedRooms(collaborationId?: string): Promise<QueuedRoom[]> {
    try {
      const clientRef = lc.getClientRef()
      const queuedRooms = await db
        .collectionGroup('rooms')
        .where('status', '==', 'queue')
        .where('client', '==', clientRef)
        .orderBy('updatedAt', 'asc')
        .withConverter(converter2<Room>())
        .get()

      if (queuedRooms.empty) {
        return []
      }

      const data = queuedRooms.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
        collaborationId: doc.ref.parent.path.split('/')[1] ?? '',
      }))

      // Get unique collaboration IDs and fetch collaborations
      const collaborationRefs = data
        .filter((v, i, a) => a.indexOf(v) === i)
        .map((room) => room.collaborationId)

      const collaborations = await Promise.all(
        collaborationRefs.map(async (collabId) => {
          const collaboration = await db
            .collection('collaborations')
            .doc(collabId)
            .withConverter(converter2<Collaboration>())
            .get()
          return { ...collaboration.data(), id: collaboration.id }
        })
      )

      // Filter WhatsApp rooms by 24-hour window
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      return data
        .filter((item) => {
          if (
            item?.accountData?.type === 'whatsapp' &&
            item?.lastCustomerInteractionAt
          ) {
            const lastInteractionDate = new Date(
              item?.lastCustomerInteractionAt.toDate()
            )
            return (
              lastInteractionDate >= yesterday && lastInteractionDate <= today
            )
          }
          return true
        })
        .map((room) => {
          const collaboration = collaborations.find(
            (collab) => collab?.id === room.collaborationId
          )
          return { ...collaboration, ...room } as QueuedRoom
        })
    } catch (error) {
      console.error('Error fetching queued rooms:', error)
      return []
    }
  }

  /**
   * Get accessible rooms by client and company access
   * Migrated from: serviceRoom.fetchAccessibleRoom
   */
  async getAccessibleRooms(
    clientId: string,
    companyAccess: UserCompanies[]
  ): Promise<Room[]> {
    let query = db.collection('rooms').withConverter(converter2<Room>())

    const hasAllCompanyAccess = companyAccess.find(
      (accessCompany) => accessCompany.id === '*'
    )

    if (!hasAllCompanyAccess) {
      const companyAccessQuery = companyAccess.map((company) =>
        createRef('company', company.id)
      )
      query
        .where('company', 'in', companyAccessQuery)
        .where('client', '==', createRef('clients', clientId))
    }

    try {
      const snaps = await query.get()
      return snaps.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      })) as Room[]
    } catch (error) {
      console.error('Error in getAccessibleRooms:', error)
      return []
    }
  }

  // ============================================================================
  // MUTATION METHODS
  // ============================================================================

  /**
   * Set room status to handled
   * Migrated from: serviceRoom.setStatusToHandled
   */
  async setStatusToHandled(
    collaborationId: string,
    roomId: string,
    handledUserId: string
  ): Promise<void> {
    try {
      const handleUserRef = createRef('users', handledUserId)
      await db
        .collection('collaborations')
        .doc(collaborationId)
        .collection('rooms')
        .doc(roomId)
        .update({
          status: 'handled',
          handledByUser: handleUserRef,
          activeHandler: handleUserRef,
          toUser: arrayUnion(handleUserRef),
        })
    } catch (error) {
      console.error('Error setStatusToHandled:', error)
    }
  }
}

export default RoomRepositoryImpl.getInstance()
